import { FormikDatePicker } from "@/components/formcontrols/DatePicker";
import FirmaFormControl from "@/components/formcontrols/FirmaFormControl";
import { TextInputFormik } from "@/components/formcontrols/InputItem";
import Modal, { ModalContext } from "@/components/Modal/Modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/select/Select";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import { PersonDto, PersonFirma } from "@/interfaces/schema";
import Button from "@/components/bricks/button/Button";
import { FormikProps, useFormik } from "formik";
import { useContext, useState } from "react";
import IsRequired from "@/components/formcontrols/common/IsRequired";
import { stringDateToPP } from "@/util/date";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import { useMutation } from "@tanstack/react-query";
import { post } from "@/api/util";
import { format, isBefore, subDays } from "date-fns";
import { validateEmail } from "@/util/validation";
import { WizardReceipt } from "../common/wizardReceipt";
import FlytfirmaIcon from "@/components/icons/FlytfirmaIcon";
import PersonFirmaRelationSelect, {
  FirmaRelationKode,
} from "@/components/formcontrols/PersonFirmaRelationSelect";
import { da } from "date-fns/locale";

type NytJobForm = {
  firmaId: string | undefined;
  periodeFraDato: string | undefined;
  personFirmatypeNr: FirmaRelationKode;
  ophoerendePersonFirmarelationer: string[];
  fortsaettendePersonFirmarelationer: string[];
  emailArbejde: string;
};

type NytJobWizardProps = {
  person: PersonDto;
  invalidateData: () => void;
};
const initialValues: NytJobForm = {
  firmaId: undefined,
  periodeFraDato: undefined,
  personFirmatypeNr: "Ansat",
  fortsaettendePersonFirmarelationer: [],
  ophoerendePersonFirmarelationer: [],
  emailArbejde: "",
};
export default function NytJobWizard(props: NytJobWizardProps) {
  const { setModalOpen } = useContext(ModalContext);
  const { invalidateData, person } = props;
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const formik = useFormik<NytJobForm>({
    initialValues,
    validate: (values: NytJobForm) => {
      if (step === 1) {
        const { firmaId, periodeFraDato, emailArbejde } = values;
        const errors: Partial<NytJobForm> = {};
        if (!firmaId) {
          errors.firmaId = "Firma skal udfyldes";
        }
        if (!periodeFraDato) {
          errors.periodeFraDato = "Startdato skal udfyldes";
        }
        if (!emailArbejde) {
          errors.emailArbejde = "Email skal udfyldes";
        }
        if (!validateEmail(emailArbejde)) {
          errors.emailArbejde = "Email er ikke gyldig";
        }
        return errors;
      } else if (step === 2) {
        const {
          periodeFraDato,
          ophoerendePersonFirmarelationer,
          fortsaettendePersonFirmarelationer,
        } = values;
        const currentFirmarelationer = periodeFraDato
          ? getCurrentFirmaRelationer(person, periodeFraDato)
          : [];
        const errors: Partial<NytJobForm> = {};
        if (
          currentFirmarelationer.length !==
          ophoerendePersonFirmarelationer.length +
            fortsaettendePersonFirmarelationer.length
        ) {
          errors.ophoerendePersonFirmarelationer = [
            "Der skal tages stilling til alle firmarelationer",
          ];
        }
        return errors;
      }
    },
    onSubmit: () => {
      if (step === 1) {
        setStep(2);
      }
      if (step === 2) {
        mutate();
      }
      if (step === 3) {
        setModalOpen(false);
        invalidateData();
      }
    },
  });
  const { values } = formik;
  const { periodeFraDato } = values;
  const currentFirmarelationer = periodeFraDato
    ? getCurrentFirmaRelationer(person, periodeFraDato)
    : [];
  const {
    isPending: isLoading,
    error,
    mutate,
    reset,
  } = useMutation<void, BackendError, void, unknown>({
    onSuccess: () => {
      setStep(3);
    },
    mutationFn: async () => {
      const body = {
        personFirmatypeNr: values.personFirmatypeNr,
        periodeFraDato: format(new Date(values.periodeFraDato!), "yyyy-MM-dd"),
        emailArbejde: values.emailArbejde,
        ophoerendePersonFirmarelationer: values.ophoerendePersonFirmarelationer,
        fortsaettendePersonFirmarelationer:
          values.fortsaettendePersonFirmarelationer,
        firmaId: values.firmaId,
      };
      await post({
        query: `person/${person.id}/wizards/nytjob`,
        body,
      });
    },
  });
  return (
    <Modal
      title={
        <>
          <FlytfirmaIcon height={18} /> Nyt job{" "}
          {step < 3 && <span className="c-modal-steps">{`${step} / 2`}</span>}
        </>
      }
      description=""
    >
      <div className="c-modal-container">
        {step === 1 && <Step1 formik={formik} />}
        {step === 2 && (
          <Step2
            formik={formik}
            currentFirmarelationer={currentFirmarelationer}
            person={person}
            isLoading={isLoading}
            backendError={error}
          />
        )}

        {step === 3 && <Step3 formik={formik} person={person} />}
        <StickyFooter>
          {step < 3 && (
            <Button
              disabled={isLoading}
              color="secondary"
              onClick={() => {
                if (step === 1) setModalOpen(false);
                if (step === 2) {
                  setStep(1);
                  reset();
                }
              }}
            >
              {step === 1 ? "Annuller" : "Tilbage"}
            </Button>
          )}
          {step === 3 && <div></div>}
          <Button
            disabled={isLoading}
            onClick={() => {
              if (step < 3) {
                formik.handleSubmit();
              } else {
                setModalOpen(false);
                invalidateData();
              }
            }}
            color="primary"
          >
            {step === 1 && "Fortsæt"}
            {step === 2 && "Opret"}
            {step === 3 && "Afslut"}
          </Button>
        </StickyFooter>
      </div>
    </Modal>
  );
}

function Step1(props: { formik: FormikProps<NytJobForm> }) {
  const { formik } = props;
  const { setFieldValue, values } = formik;
  return (
    <div className="c-modal-container-inner c-item-list mt-3">
      <div>
        <FormikDatePicker
          formik={formik}
          config={{
            component: "datepicker",
            id: "periodeFraDato",
            label: "Startdato",
            isRequired: true,
            initialValue: "",
          }}
        />
      </div>
      <div className="flex-100">
        <FirmaFormControl
          config={{
            component: "firma",
            id: "firmaId",
            label: "Nyt firma",
            isRequired: true,
          }}
          formik={formik}
          value={values.firmaId}
        />
      </div>
      <div className="flex-100">
        <PersonFirmaRelationSelect
          currentValue={values.personFirmatypeNr}
          setValue={(value) => setFieldValue("personFirmatypeNr", value, true)}
        />
      </div>
      <div className="flex-100">
        <TextInputFormik
          config={{
            component: "textinput",
            id: "emailArbejde",
            label: "Email arbejde",
            isRequired: true,
            initialValue: "",
          }}
          formik={formik}
        />
      </div>
    </div>
  );
}
function Step2(props: {
  formik: FormikProps<NytJobForm>;
  person: PersonDto;
  currentFirmarelationer: PersonFirma[];
  isLoading: boolean;
  backendError: BackendError | null;
}) {
  const { currentFirmarelationer, formik, person, isLoading, backendError } =
    props;
  const { setValues, values } = formik;
  return (
    <div className="c-modal-container-inner c-item-list mt-3">
      <fieldset disabled={isLoading}>
        <h3 className="mb-3 h2">Gældende og fremtidige firmarelationer</h3>
        {currentFirmarelationer.length === 0 && (
          <p>
            {person.fuldtNavn} har ingen gældende eller fremtidige
            firmarelationer på startdatoen (
            {stringDateToPP(values.periodeFraDato!)}) for den nye firmarelation.
          </p>
        )}
        {currentFirmarelationer.length > 0 && (
          <>
            <p>
              {person.fuldtNavn} har {currentFirmarelationer.length} gældende
              eller{" "}
              {currentFirmarelationer.length > 1
                ? "fremtidige firmarelationer"
                : "fremtidig firmarelation"}{" "}
              i forhold til startdatoen (
              {stringDateToPP(values.periodeFraDato!)}) for den nye
              firmarelation.
            </p>
            <p>Tag stilling til, hvad der skal ske med firmarelationerne:</p>
          </>
        )}
        {currentFirmarelationer.map((f, index) => {
          const erFremtidig = isBefore(
            new Date(values.periodeFraDato!),
            new Date(f.periode.fraDato),
          );
          return (
            <div
              key={f.id}
              className={`p-3 bg-white shadow form-control  ${
                index > 0 ? "mt-3" : ""
              }`}
            >
              <h4 className="mb-2">{f.firma.navn1}</h4>
              <div className="d-flex align-items-center gap-3 justify-content-evenly">
                <div className="w-50">
                  <div>Relation: {f.personfirmatypeNrNavigation?.navn}</div>
                  <div>Startdato: {stringDateToPP(f.periode.fraDato)}</div>
                  <div>
                    Slutdato: {stringDateToPP(f.periode.tilDatoInklusiv)}
                  </div>
                </div>
                <div className="w-50">
                  <Select
                    defaultValue={
                      values.ophoerendePersonFirmarelationer.includes(f.id)
                        ? "Ophører"
                        : values.fortsaettendePersonFirmarelationer.includes(
                              f.id,
                            )
                          ? "Fortsætter"
                          : undefined
                    }
                    onValueChange={(value) => {
                      switch (value) {
                        case "Fortsætter": {
                          setValues(
                            {
                              ...values,
                              ophoerendePersonFirmarelationer:
                                values.ophoerendePersonFirmarelationer.filter(
                                  (id) => id !== f.id,
                                ),
                              fortsaettendePersonFirmarelationer: [
                                ...values.fortsaettendePersonFirmarelationer,
                                f.id,
                              ],
                            },
                            true,
                          );
                          break;
                        }
                        case "Ophører": {
                          formik.setValues(
                            {
                              ...values,
                              fortsaettendePersonFirmarelationer:
                                values.fortsaettendePersonFirmarelationer.filter(
                                  (id) => id !== f.id,
                                ),
                              ophoerendePersonFirmarelationer: [
                                ...values.ophoerendePersonFirmarelationer,
                                f.id,
                              ],
                            },
                            true,
                          );
                          break;
                        }
                      }
                    }}
                  >
                    <label className="form-label">
                      {"Firmarelation"} <IsRequired />
                    </label>
                    <SelectTrigger placeholder="Vælg" />
                    <SelectContent>
                      <SelectItem value={"Fortsætter"}>Bibeholdes</SelectItem>
                      <SelectItem value={"Ophører"}>
                        {erFremtidig
                          ? "Slettes"
                          : "Ophører (" +
                            format(
                              subDays(new Date(values.periodeFraDato!), 1),
                              "PP",
                              { locale: da },
                            ) +
                            ")"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          );
        })}
        {(backendError ||
          formik.errors.ophoerendePersonFirmarelationer?.length) && (
          <div className="position-relative">
            <ValidationErrorMessage
              touched={true}
              errorMessage={
                backendError && typeof backendError.data === "object"
                  ? backendError.data.Message
                  : backendError && typeof backendError.data === "string"
                    ? backendError.data
                    : formik.errors.ophoerendePersonFirmarelationer?.length
                      ? formik.errors.ophoerendePersonFirmarelationer[0]
                      : undefined
              }
            />
          </div>
        )}
      </fieldset>
    </div>
  );
}
function Step3(props: { formik: FormikProps<NytJobForm>; person: PersonDto }) {
  const { formik, person } = props;

  return (
    <WizardReceipt heading="Nyt job oprettet" icon="success" className="">
      <p className="text-muted text-center">
        {person.fuldtNavn} er registreret med et nyt job fra den{" "}
        {stringDateToPP(formik.values.periodeFraDato!)}
      </p>
    </WizardReceipt>
  );
}

const getCurrentFirmaRelationer = (
  person: PersonDto,
  date: string,
): PersonFirma[] => {
  return (
    person.personFirma?.filter((f) =>
      isBefore(new Date(date), new Date(f.periode.tilDatoInklusiv)),
    ) ?? []
  );
};

type BackendError =
  { data: string } | { data: { Message: string; StatusCode: number } };
